import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Badge,
  Button,
  Input,
  List,
  Typography,
  Tooltip,
  Space,
  Tag,
} from "antd";
import {
  SendOutlined,
  CloseOutlined,
  MessageOutlined,
  UserOutlined,
  DeleteOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  ShoppingOutlined,
  EuroCircleOutlined,
  InboxOutlined,
  TruckOutlined,
  EyeOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { Text, Paragraph } = Typography;

const STORAGE_KEY = "admin_chat_messages_v6";
const CONTEXT_KEY = "admin_chat_context_v1";
const TOP_SEARCHED_KEY = "admin_chat_top_searched_v1";

const ADMIN_ORDER_VISIBLE_LIMIT = 4;

const SUGGESTION_BLOCKLIST = [
  "hi",
  "hello",
  "hey",
  "assalam",
  "assalam o alaikum",
  "salam",
  "thanks",
  "thank you",
  "bye",
  "goodbye",
  "how are you",
  "how r you",
];

const QUICK_PROMPTS = [
  "orders today",
  "revenue this month",
  "urgent orders",
  "stock under 5",
];

const DEFAULT_SUGGESTIONS = [
  "orders today",
  "orders last 7 days",
  "revenue this month",
  "order stats this month",
  "urgent orders",
  "out of stock",
  "stock under 5",
  "complaints pending",
  "production queue ottoman",
  "production queue cloud",
  "orders by size this month",
  "users count by role",
  "storage prices",
  "top products this month",
  "top customers this month",
  "help",
];

const PROMPT_GROUPS = [
  {
    title: "Orders",
    icon: <ShoppingOutlined />,
    prompts: [
      "orders today",
      "orders last 7 days",
      "urgent orders",
      "order stats this month",
    ],
  },
  {
    title: "Revenue",
    icon: <BarChartOutlined />,
    prompts: [
      "revenue today",
      "revenue this month",
      "top products this month",
      "top customers this month",
    ],
  },
  {
    title: "Stock",
    icon: <AppstoreOutlined />,
    prompts: [
      "out of stock",
      "stock under 5",
      "storage prices",
      "products low stock",
    ],
  },
  {
    title: "Production",
    icon: <ThunderboltOutlined />,
    prompts: [
      "production queue ottoman",
      "production queue cloud",
      "pending dispatch orders",
      "orders by size this month",
    ],
  },
  {
    title: "Support",
    icon: <WarningOutlined />,
    prompts: [
      "complaints pending",
      "complaints resolved this month",
      "users count by role",
      "help",
    ],
  },
];

function titleize(key) {
  return String(key || "")
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeKey(key) {
  return String(key || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function getValue(row, possibleKeys = []) {
  if (!row || typeof row !== "object") return undefined;

  for (const key of possibleKeys) {
    if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  }

  const rowKeys = Object.keys(row);
  const normalizedMap = new Map(rowKeys.map((k) => [normalizeKey(k), k]));

  for (const key of possibleKeys) {
    const realKey = normalizedMap.get(normalizeKey(key));
    if (realKey) return row[realKey];
  }

  return undefined;
}

function cleanText(v, fallback = "-") {
  if (v === null || v === undefined || v === "") return fallback;
  return String(v);
}

function formatTime(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMins = Math.floor((now - date) / 60000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;

  return date.toLocaleDateString();
}

function toMoneyNumber(v) {
  const n = Number(String(v ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatCurrency(v) {
  const n = toMoneyNumber(v);

  if (!Number.isFinite(n)) return v ?? "-";

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

function statusTag(value) {
  const raw = cleanText(value, "-");
  const s = raw.toLowerCase();

  if (!s || s === "-") return <Tag style={styles.statusNeutral}>-</Tag>;

  if (
    ["pending", "processing", "in queue", "queued", "in progress", "in-progress"].some((x) =>
      s.includes(x)
    )
  ) {
    return <Tag style={styles.statusProcessing}>{raw}</Tag>;
  }

  if (
    ["approved", "completed", "resolved", "shipped", "complete", "delivered", "dispatched"].some(
      (x) => s.includes(x)
    )
  ) {
    return <Tag style={styles.statusSuccess}>{raw}</Tag>;
  }

  if (["rejected", "cancelled", "canceled", "failed"].some((x) => s.includes(x))) {
    return <Tag style={styles.statusDanger}>{raw}</Tag>;
  }

  if (["urgent", "high"].some((x) => s.includes(x))) {
    return <Tag style={styles.statusWarning}>{raw}</Tag>;
  }

  return <Tag style={styles.statusNeutral}>{raw}</Tag>;
}

function sanitizeFilename(name) {
  const base = String(name || "export").trim() || "export";

  return base
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

function csvEscape(value) {
  const s = value == null ? "" : String(value);

  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;

  return s;
}

function exportTableToCsv(table) {
  if (!table?.rows?.length) return;

  const exportCols = (table.columns || [])
    .map((c) => {
      const key = c.dataIndex || c.key;
      const title = typeof c.title === "string" ? c.title : titleize(key);

      return { key, title };
    })
    .filter((c) => c.key && c.key !== "__rowKey" && c.key !== "__actions");

  const header = exportCols.map((c) => csvEscape(c.title)).join(",");

  const lines = table.rows.map((row) => {
    return exportCols
      .map((c) => {
        const v = row?.[c.key];

        if (v && typeof v === "object") return csvEscape(JSON.stringify(v));

        return csvEscape(v);
      })
      .join(",");
  });

  const csv = "\ufeff" + [header, ...lines].join("\n");

  const filename = `${sanitizeFilename(table.title || "results")}.csv`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

function loadTopSearches() {
  try {
    const raw = localStorage.getItem(TOP_SEARCHED_KEY);
    const obj = raw ? JSON.parse(raw) : {};

    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}

function saveTopSearches(obj) {
  try {
    localStorage.setItem(TOP_SEARCHED_KEY, JSON.stringify(obj));
  } catch {
    // ignore
  }
}

function normalizeQuery(q) {
  return String(q || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function isBlockedSuggestion(q) {
  const x = normalizeQuery(q);

  return SUGGESTION_BLOCKLIST.some(
    (b) => x === normalizeQuery(b) || x.startsWith(normalizeQuery(b) + " ")
  );
}

function trackTopQuery(q) {
  const query = normalizeQuery(q);

  if (!query) return;
  if (isBlockedSuggestion(query)) return;

  const data = loadTopSearches();
  data[query] = (data[query] || 0) + 1;

  const keys = Object.keys(data);

  if (keys.length > 200) {
    keys.sort((a, b) => (data[a] || 0) - (data[b] || 0));

    const removeCount = keys.length - 200;

    for (let i = 0; i < removeCount; i++) {
      delete data[keys[i]];
    }
  }

  saveTopSearches(data);
}

function getTopSuggestions(limit = 8) {
  const data = loadTopSearches();

  const entries = Object.entries(data)
    .filter(([q]) => q && !isBlockedSuggestion(q))
    .sort((a, b) => (b[1] || 0) - (a[1] || 0))
    .map(([q]) => q);

  const base = entries.length ? entries : DEFAULT_SUGGESTIONS;

  const uniq = [];

  for (const s of base) {
    const v = normalizeQuery(s);

    if (!v || isBlockedSuggestion(v)) continue;
    if (!uniq.includes(v)) uniq.push(v);
    if (uniq.length >= limit) break;
  }

  if (!uniq.includes("help")) uniq.push("help");

  return uniq.slice(0, limit);
}

function buildColumnsFromServer(columnsFromServer = [], rows = []) {
  const hiddenKeys = new Set([
    "id",
    "createdat",
    "updatedat",
    "userid",
    "productid",
    "__rowkey",
  ]);

  if (columnsFromServer.length > 0) {
    return columnsFromServer
      .map((c) => {
        const key = c.dataIndex || c.key;

        return {
          title: c.title || titleize(key),
          dataIndex: key,
          key,
        };
      })
      .filter((c) => !hiddenKeys.has(normalizeKey(c.key)));
  }

  return Object.keys(rows?.[0] || {})
    .filter((k) => !hiddenKeys.has(normalizeKey(k)))
    .map((k) => ({
      title: titleize(k),
      dataIndex: k,
      key: k,
    }));
}

function isOrderIntent(intent, title, rows = []) {
  const normalizedIntent = String(intent || "").toUpperCase();
  const normalizedTitle = String(title || "").toLowerCase();

  if (["ORDERS_LIST", "ORDER_LOOKUP", "PRODUCTION_QUEUE"].includes(normalizedIntent)) {
    return true;
  }

  if (normalizedTitle.includes("order")) return true;

  const first = rows?.[0] || {};
  const keys = Object.keys(first).map((k) => normalizeKey(k));

  return keys.some((k) => ["orderno", "orderid", "trackingid", "customerref"].includes(k));
}

function getOrderNo(row) {
  return cleanText(
    getValue(row, [
      "orderNo",
      "order_no",
      "Order No",
      "orderNumber",
      "order_number",
      "Order Number",
      "orderId",
      "Order Id",
      "id",
    ])
  );
}

function getTrackingId(row) {
  return cleanText(
    getValue(row, [
      "trackingId",
      "tracking_id",
      "Tracking Id",
      "Tracking ID",
      "trackingNo",
      "tracking_no",
    ]),
    ""
  );
}

function getCustomer(row) {
  return cleanText(
    getValue(row, [
      "customer",
      "Customer",
      "customerName",
      "customer_name",
      "Customer Name",
      "company",
      "Company",
      "companyName",
      "company_name",
    ]),
    ""
  );
}

function getProduct(row) {
  return cleanText(
    getValue(row, [
      "product",
      "Product",
      "productName",
      "product_name",
      "Product Name",
      "item",
      "Item",
      "name",
    ])
  );
}

function getQty(row) {
  return cleanText(getValue(row, ["qty", "Qty", "quantity", "Quantity"]), "1");
}

function getAmount(row) {
  return getValue(row, [
    "price",
    "Price",
    "amount",
    "Amount",
    "total",
    "Total",
    "totalPrice",
    "total_price",
    "orderTotal",
    "order_total",
    "revenue",
    "Revenue",
  ]);
}

function getStatus(row) {
  return cleanText(
    getValue(row, [
      "status",
      "Status",
      "orderStatus",
      "order_status",
      "productionStatus",
      "production_status",
      "Production Status",
      "deliveryStatus",
      "delivery_status",
      "Delivery Status",
    ])
  );
}

function getDeliveryStatus(row) {
  return cleanText(
    getValue(row, [
      "deliveryStatus",
      "delivery_status",
      "Delivery Status",
      "shippingStatus",
      "shipping_status",
    ]),
    ""
  );
}

function bestOrderToken(row) {
  return (
    getOrderNo(row) ||
    getTrackingId(row) ||
    cleanText(getValue(row, ["customerRef", "customer_ref", "Customer Ref"]), "")
  );
}

function getOrderStats(rows = [], rowCount) {
  const totalOrders = Number(rowCount ?? rows.length) || rows.length;

  const totalValue = rows.reduce((sum, row) => {
    return sum + toMoneyNumber(getAmount(row));
  }, 0);

  const dispatched = rows.filter((row) => {
    const s = `${getStatus(row)} ${getDeliveryStatus(row)}`.toLowerCase();

    return (
      s.includes("dispatched") ||
      s.includes("delivered") ||
      s.includes("shipped") ||
      s.includes("out for delivery")
    );
  }).length;

  const pending = Math.max(totalOrders - dispatched, 0);

  return {
    totalOrders,
    totalValue,
    pending,
    dispatched,
  };
}

function buildShortAnswer(answerText, table) {
  if (!table?.rows?.length) return answerText;

  const title = String(table.title || "").toLowerCase();

  if (title.includes("order")) {
    return "Here’s what I found for today.";
  }

  return answerText || "Here’s a quick summary.";
}

function MetricCard({ icon, label, value, tone }) {
  return (
    <div style={{ ...styles.metricCard, ...styles[`metricCard_${tone}`] }}>
      <div style={{ ...styles.metricIcon, ...styles[`metricIcon_${tone}`] }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={styles.metricValue}>{value}</div>
        <div style={styles.metricLabel}>{label}</div>
      </div>
    </div>
  );
}

function PromptBuilder({ onPrompt, loading }) {
  return (
    <div style={styles.promptBuilder}>
      <div style={styles.promptBuilderHeader}>
        <div>
          <div style={styles.promptBuilderTitle}>Prompt Builder</div>
          <div style={styles.promptBuilderSubtitle}>
            Choose a quick admin question to get real-time data.
          </div>
        </div>
      </div>

      <div style={styles.promptGroupGrid}>
        {PROMPT_GROUPS.map((group) => (
          <div key={group.title} style={styles.promptGroupCard}>
            <div style={styles.promptGroupTitle}>
              <span style={styles.promptGroupIcon}>{group.icon}</span>
              {group.title}
            </div>

            <div style={styles.promptButtons}>
              {group.prompts.map((prompt) => (
                <Button
                  key={prompt}
                  size="small"
                  style={styles.promptButton}
                  onClick={() => onPrompt(prompt)}
                  disabled={loading}
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrderOverviewCard({ table, onSend, loading }) {
  const rows = Array.isArray(table?.rows) ? table.rows : [];
  const visibleRows = rows.slice(0, ADMIN_ORDER_VISIBLE_LIMIT);
  const stats = getOrderStats(rows, table?.rowCount);

  return (
    <div style={styles.orderCard}>
      <div style={styles.orderCardHeader}>
        <div>
          <div style={styles.orderTitle}>Today’s Orders</div>
          <div style={styles.orderSubtitle}>
            Showing latest {Math.min(visibleRows.length, ADMIN_ORDER_VISIBLE_LIMIT)} of{" "}
            {stats.totalOrders}
          </div>
        </div>

        <div style={styles.liveStatus}>
          <span style={styles.liveDot} />
          Updated just now
        </div>
      </div>

      <div style={styles.metricsGrid}>
        <MetricCard
          tone="blue"
          icon={<ShoppingOutlined />}
          value={stats.totalOrders}
          label="Orders"
        />

        <MetricCard
          tone="green"
          icon={<EuroCircleOutlined />}
          value={formatCurrency(stats.totalValue)}
          label="Total Value"
        />

        <MetricCard
          tone="orange"
          icon={<InboxOutlined />}
          value={stats.pending}
          label="Pending"
        />

        <MetricCard
          tone="purple"
          icon={<TruckOutlined />}
          value={stats.dispatched}
          label="Dispatched"
        />
      </div>

      <div style={styles.compactTable}>
        <div style={styles.compactTableHead}>
          <span>Order</span>
          <span>Product</span>
          <span>Amount</span>
          <span>Status</span>
        </div>

        {visibleRows.map((row, index) => {
          const orderNo = getOrderNo(row);
          const customer = getCustomer(row);
          const product = getProduct(row);
          const qty = getQty(row);
          const amount = getAmount(row);
          const status = getStatus(row);

          return (
            <button
              key={row.__rowKey ?? index}
              type="button"
              style={styles.orderRow}
              onClick={() => onSend(`order status ${bestOrderToken(row)}`.trim())}
              disabled={loading}
            >
              <span style={styles.orderNoBlock}>
                <Text style={styles.orderNo}>{orderNo}</Text>
                {customer ? <Text style={styles.orderCustomer}>{customer}</Text> : null}
              </span>

              <span style={styles.productBlock}>
                <Text style={styles.productName}>{product}</Text>
                <Text style={styles.productQty}>Qty {qty}</Text>
              </span>

              <span style={styles.amountText}>{formatCurrency(amount)}</span>

              <span>{statusTag(status)}</span>
            </button>
          );
        })}
      </div>

      <div style={styles.orderFooter}>
        <Button
          icon={<EyeOutlined />}
          style={styles.viewAllButton}
          onClick={() => onSend("show all orders today")}
          disabled={loading}
        >
          View all orders
        </Button>

        <Button
          icon={<DownloadOutlined />}
          style={styles.exportButton}
          onClick={() => exportTableToCsv(table)}
          disabled={!rows.length}
        >
          Export
        </Button>
      </div>
    </div>
  );
}

function GenericSummaryCard({ table, onSend, loading }) {
  const rows = Array.isArray(table?.rows) ? table.rows : [];
  const columns = Array.isArray(table?.columns) ? table.columns.slice(0, 4) : [];
  const visibleRows = rows.slice(0, 5);

  return (
    <div style={styles.orderCard}>
      <div style={styles.orderCardHeader}>
        <div>
          <div style={styles.orderTitle}>{table?.title || "Results"}</div>
          <div style={styles.orderSubtitle}>
            Showing {visibleRows.length} of {table?.rowCount ?? rows.length}
          </div>
        </div>

        <Button
          size="small"
          icon={<DownloadOutlined />}
          style={styles.exportButton}
          onClick={() => exportTableToCsv(table)}
          disabled={!rows.length}
        >
          Export
        </Button>
      </div>

      <div style={styles.genericList}>
        {visibleRows.map((row, rowIndex) => (
          <div key={row.__rowKey ?? rowIndex} style={styles.genericRow}>
            {columns.map((col) => {
              const key = col.dataIndex || col.key;
              const value = row?.[key];

              return (
                <div key={key} style={styles.genericCell}>
                  <Text style={styles.genericLabel}>{col.title || titleize(key)}</Text>
                  <Text style={styles.genericValue}>{cleanText(value)}</Text>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div style={styles.orderFooter}>
        <Button
          icon={<EyeOutlined />}
          style={styles.viewAllButton}
          onClick={() => onSend(`show full ${table?.title || "results"}`)}
          disabled={loading}
        >
          View details
        </Button>

        <Button
          icon={<ReloadOutlined />}
          style={styles.exportButton}
          onClick={() => onSend("refresh")}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>
    </div>
  );
}

export default function ChatWidget({ visible = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);

      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);

  const cacheRef = useRef(new Map());
  const unreadRef = useRef(0);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  const getContext = () => {
    try {
      const raw = sessionStorage.getItem(CONTEXT_KEY);

      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const setContext = (ctx) => {
    sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(ctx || {}));
  };

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(msgs));

    if (listRef.current) {
      setTimeout(() => {
        listRef.current.scrollTo({
          top: listRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    }
  }, [msgs]);

  useEffect(() => {
    if (open) {
      unreadRef.current = 0;

      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", onKey);

    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const clearConversation = () => {
    setMsgs([]);
    cacheRef.current.clear();
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(CONTEXT_KEY);
  };

  const pushAssistant = (payload) => {
    setMsgs((m) => [
      ...m,
      {
        id: `a-${Date.now()}`,
        role: "assistant",
        ts: Date.now(),
        text: payload.text || "",
        table: payload.table || null,
        suggestions: payload.suggestions || [],
        meta: payload.meta || {},
        kind: payload.kind || "text",
      },
    ]);

    if (payload?.meta?.intent) setContext({ lastIntent: payload.meta.intent });
  };

  const sendMessage = async (overrideText) => {
    const question = (overrideText ?? q).trim();

    if (!question) return;

    trackTopQuery(question);

    setMsgs((m) => [
      ...m,
      {
        id: `u-${Date.now()}`,
        role: "user",
        text: question,
        ts: Date.now(),
      },
    ]);

    setQ("");

    if (cacheRef.current.has(question)) {
      pushAssistant(cacheRef.current.get(question));
      return;
    }

    setLoading(true);

    try {
      const context = getContext();
      const { data } = await axiosInstance.post("/admin/chat", {
        question,
        context,
      });

      const answerText = data?.answer || "OK";
      const result = data?.result || {};
      const rows = Array.isArray(result.rows) ? result.rows : [];
      const serverCols = Array.isArray(result.columns) ? result.columns : [];
      const columns = buildColumnsFromServer(serverCols, rows);
      const intent = result?.meta?.intent;
      const title = result.title || "Results";

      const preparedTable =
        rows.length > 0
          ? {
              title,
              rows: rows.map((r, i) => ({
                __rowKey: i,
                ...r,
              })),
              columns,
              rowCount: result.rowCount ?? rows.length,
              isOrderCard: isOrderIntent(intent, title, rows),
            }
          : null;

      const payload = {
        text: buildShortAnswer(answerText, preparedTable),
        kind: result.kind || "text",
        suggestions: result.suggestions || [],
        meta: result.meta || {},
        table: preparedTable,
      };

      cacheRef.current.set(question, payload);
      pushAssistant(payload);

      if (!open) unreadRef.current += 1;
    } catch (err) {
      const status = err?.response?.status;

      const msg =
        status === 429
          ? "Too many requests. Please wait a moment and try again."
          : status === 401
          ? "Session expired. Please log in again."
          : "I'm having trouble connecting. Please try again.";

      pushAssistant({
        text: msg,
        kind: "text",
        suggestions: ["help"],
      });

      if (!open) unreadRef.current += 1;
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!visible) return null;

  const emptySuggestions = getTopSuggestions(8);
  const TOOLTIP_Z = 10050;

  return (
    <>
      {open && <div style={styles.backdrop} onMouseDown={() => setOpen(false)} />}

      {!open && (
        <div style={styles.floatingButtonWrap}>
          <Tooltip title="Ask Assistant" placement="left" color="#2563eb" zIndex={TOOLTIP_Z}>
            <Badge dot={unreadRef.current > 0} offset={[-5, 5]} size="small" color="#3b82f6">
              <Button
                shape="circle"
                size="large"
                onClick={() => setOpen(true)}
                style={styles.floatingButton}
                className="chat-bubble-button"
                icon={<MessageOutlined style={{ fontSize: 24, color: "#fff" }} />}
              />
            </Badge>
          </Tooltip>
        </div>
      )}

      {open && (
        <div
          ref={panelRef}
          style={styles.panelContainer}
          className="chat-panel"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div style={styles.panelHeader}>
            <Space size={14}>
              <div style={styles.headerAvatarWrapper}>
                <Avatar size={48} icon={<RobotOutlined />} style={styles.headerAvatar} />
                <div style={styles.statusIndicator} />
              </div>

              <div>
                <div style={styles.headerTitle}>AI Assistant</div>
                <Space size={5}>
                  <CheckCircleOutlined style={styles.statusIcon} />
                  <Text style={styles.headerSubtitle}>Online · Ready to help</Text>
                </Space>
              </div>
            </Space>

            <Space size={8}>
              <Tooltip title="Clear conversation" color="#2563eb" zIndex={TOOLTIP_Z}>
                <Button
                  size="middle"
                  icon={<DeleteOutlined />}
                  onClick={clearConversation}
                  style={styles.iconButton}
                />
              </Tooltip>

              <Tooltip title="Close" color="#2563eb" zIndex={TOOLTIP_Z}>
                <Button
                  size="middle"
                  icon={<CloseOutlined />}
                  onClick={() => setOpen(false)}
                  style={styles.iconButton}
                />
              </Tooltip>
            </Space>
          </div>

          <div style={styles.panelBody} ref={listRef}>
            {msgs.length === 0 ? (
              <div style={styles.emptyState}>
                <div style={styles.emptyTopCard}>
                  <div style={styles.emptyIconWrapper}>
                    <RobotOutlined style={styles.emptyIcon} />
                  </div>

                  <Text strong style={styles.emptyTitle}>
                    What do you want to check?
                  </Text>

                  <Text style={styles.emptyText}>
                    Use ready prompts for orders, revenue, stock, production and support.
                  </Text>

                  <div style={styles.suggestionChips}>
                    {emptySuggestions.map((s) => (
                      <Button
                        key={s}
                        block
                        size="middle"
                        style={styles.suggestionTag}
                        onClick={() => sendMessage(s)}
                        disabled={loading}
                      >
                        {s}
                      </Button>
                    ))}
                  </div>
                </div>

                <PromptBuilder onPrompt={sendMessage} loading={loading} />
              </div>
            ) : (
              <List
                dataSource={msgs}
                split={false}
                renderItem={(m, index) => {
                  const isUser = m.role === "user";
                  const showAvatar = index === 0 || msgs[index - 1]?.role !== m.role;

                  return (
                    <List.Item style={styles.messageItem}>
                      <div
                        style={{
                          ...styles.messageContainer,
                          flexDirection: isUser ? "row-reverse" : "row",
                        }}
                      >
                        {showAvatar ? (
                          <div style={styles.avatarContainer}>
                            <Avatar
                              size={38}
                              icon={isUser ? <UserOutlined /> : <RobotOutlined />}
                              style={{
                                ...styles.messageAvatar,
                                background: isUser
                                  ? styles.userGradient
                                  : styles.assistantGradient,
                              }}
                            />
                          </div>
                        ) : (
                          <div style={{ width: 38, margin: "0 8px" }} />
                        )}

                        <div
                          style={{
                            ...styles.messageBubble,
                            ...(isUser ? styles.userBubble : styles.assistantBubble),
                            maxWidth: showAvatar ? "calc(100% - 60px)" : "100%",
                          }}
                        >
                          {showAvatar && (
                            <div style={styles.messageHeader}>
                              <Text
                                strong
                                style={{
                                  fontSize: 13,
                                  color: isUser ? "#fff" : "#111827",
                                }}
                              >
                                {isUser ? "You" : "AI Assistant"}
                              </Text>

                              <span style={styles.messageHeaderRight}>
                                <ClockCircleOutlined
                                  style={{
                                    fontSize: 10,
                                    color: isUser ? "rgba(255,255,255,0.72)" : "#94a3b8",
                                  }}
                                />

                                <Text
                                  style={{
                                    fontSize: 11,
                                    color: isUser ? "rgba(255,255,255,0.72)" : "#64748b",
                                  }}
                                >
                                  {formatTime(m.ts)}
                                </Text>
                              </span>
                            </div>
                          )}

                          {m.text ? (
                            <Paragraph
                              style={{
                                margin: 0,
                                whiteSpace: "pre-wrap",
                                color: isUser ? "#fff" : "#1e293b",
                                fontSize: 14,
                                lineHeight: 1.55,
                              }}
                            >
                              {m.text}
                            </Paragraph>
                          ) : null}

                          {!isUser && m.table?.rows?.length > 0 ? (
                            <div style={{ marginTop: 12 }}>
                              {m.table.isOrderCard ? (
                                <OrderOverviewCard
                                  table={m.table}
                                  onSend={sendMessage}
                                  loading={loading}
                                />
                              ) : (
                                <GenericSummaryCard
                                  table={m.table}
                                  onSend={sendMessage}
                                  loading={loading}
                                />
                              )}
                            </div>
                          ) : null}

                          {!isUser && Array.isArray(m.suggestions) && m.suggestions.length > 0 ? (
                            <div style={styles.inlineSuggestions}>
                              {m.suggestions.slice(0, 6).map((s) => (
                                <Button
                                  key={s}
                                  size="small"
                                  style={styles.inlineSuggestionButton}
                                  onClick={() => sendMessage(s)}
                                  disabled={loading}
                                >
                                  {s}
                                </Button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </div>

          <div style={styles.quickPromptBar}>
            {QUICK_PROMPTS.map((prompt) => (
              <Button
                key={prompt}
                size="small"
                style={styles.quickPromptButton}
                onClick={() => sendMessage(prompt)}
                disabled={loading}
              >
                {prompt}
              </Button>
            ))}
          </div>

          <div style={styles.panelFooter}>
            <div style={styles.inputWrapper}>
              <Input.TextArea
                ref={inputRef}
                autoSize={{ minRows: 1, maxRows: 4 }}
                placeholder="Type your message..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={handleKeyDown}
                style={styles.textArea}
                disabled={loading}
              />

              <Button
                type="primary"
                shape="circle"
                icon={<SendOutlined />}
                loading={loading}
                onClick={() => sendMessage()}
                disabled={!q.trim()}
                style={styles.sendButton}
              />
            </div>

            <div style={styles.footerInfo}>
              <Text style={styles.footerText}>
                <span style={styles.keyHint}>↵</span> Enter
              </Text>

              <Text style={styles.footerText}>
                <span style={styles.keyHint}>⇧</span> Shift + Enter
              </Text>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "transparent",
    zIndex: 9998,
  },

  floatingButtonWrap: {
    position: "fixed",
    right: 28,
    bottom: 28,
    zIndex: 9999,
  },

  floatingButton: {
    background: "linear-gradient(145deg,#635bff,#2563eb)",
    border: "none",
    width: 68,
    height: 68,
    boxShadow: "0 18px 45px rgba(37,99,235,0.38)",
  },

  panelContainer: {
    position: "fixed",
    right: 28,
    bottom: 28,
    width: 760,
    maxWidth: "calc(100vw - 56px)",
    height: 720,
    maxHeight: "calc(100vh - 56px)",
    zIndex: 9999,
    background: "#ffffff",
    borderRadius: 30,
    boxShadow: "0 35px 90px rgba(15,23,42,0.22)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    border: "1px solid rgba(226,232,240,0.95)",
  },

  panelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid #e5e7eb",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.86) 100%)",
  },

  headerAvatarWrapper: {
    position: "relative",
  },

  headerAvatar: {
    background: "linear-gradient(145deg,#635bff,#2563eb)",
    color: "#fff",
    border: "3px solid #fff",
    boxShadow: "0 14px 28px rgba(37,99,235,0.26)",
  },

  statusIndicator: {
    position: "absolute",
    bottom: 2,
    right: 2,
    width: 12,
    height: 12,
    background: "#10b981",
    borderRadius: "50%",
    border: "2px solid #fff",
  },

  headerTitle: {
    fontWeight: 800,
    fontSize: 20,
    color: "#0f172a",
    letterSpacing: "-0.02em",
  },

  headerSubtitle: {
    fontSize: 13,
    color: "#64748b",
  },

  statusIcon: {
    fontSize: 12,
    color: "#10b981",
  },

  iconButton: {
    border: "1px solid #e5e7eb",
    color: "#64748b",
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 6px 16px rgba(15,23,42,0.04)",
  },

  panelBody: {
    flex: 1,
    overflowY: "auto",
    padding: 24,
    background:
      "radial-gradient(circle at top left, rgba(99,91,255,0.05), transparent 32%), linear-gradient(180deg,#f8fafc 0%,#ffffff 100%)",
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    minHeight: "100%",
  },

  emptyTopCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    padding: "16px 8px",
  },

  emptyIconWrapper: {
    width: 92,
    height: 92,
    borderRadius: 30,
    background: "linear-gradient(145deg,#eef2ff,#ffffff)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 18,
    boxShadow: "0 20px 45px rgba(99,91,255,0.16)",
    border: "1px solid #e0e7ff",
  },

  emptyIcon: {
    fontSize: 46,
    color: "#4f46e5",
  },

  emptyTitle: {
    fontSize: 24,
    color: "#0f172a",
    marginBottom: 8,
    fontWeight: 800,
  },

  emptyText: {
    color: "#64748b",
    fontSize: 14,
    maxWidth: 430,
  },

  suggestionChips: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 10,
    width: "100%",
    maxWidth: 600,
    marginTop: 18,
  },

  suggestionTag: {
    borderRadius: 999,
    height: 40,
    border: "1px solid #dbeafe",
    background: "#ffffff",
    color: "#2563eb",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  promptBuilder: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 24,
    padding: 18,
    boxShadow: "0 18px 45px rgba(15,23,42,0.07)",
  },

  promptBuilderHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
  },

  promptBuilderTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: "#111827",
  },

  promptBuilderSubtitle: {
    marginTop: 3,
    fontSize: 12,
    color: "#64748b",
  },

  promptGroupGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },

  promptGroupCard: {
    border: "1px solid #eef2f7",
    borderRadius: 18,
    padding: 14,
    background:
      "linear-gradient(180deg, rgba(248,250,252,0.9), rgba(255,255,255,1))",
  },

  promptGroupTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#111827",
    fontWeight: 800,
    marginBottom: 10,
  },

  promptGroupIcon: {
    width: 28,
    height: 28,
    borderRadius: 10,
    background: "#eef2ff",
    color: "#4f46e5",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  promptButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  promptButton: {
    borderRadius: 999,
    border: "1px solid #dbeafe",
    color: "#2563eb",
    background: "#ffffff",
    fontWeight: 600,
  },

  messageItem: {
    padding: "7px 0",
    border: "none",
  },

  messageContainer: {
    display: "flex",
    gap: 10,
    width: "100%",
  },

  avatarContainer: {
    flexShrink: 0,
  },

  messageAvatar: {
    marginTop: 4,
    border: "2px solid #fff",
    boxShadow: "0 12px 24px rgba(15,23,42,0.08)",
  },

  userGradient: "linear-gradient(145deg,#2563eb,#4f46e5)",
  assistantGradient: "linear-gradient(145deg,#635bff,#2563eb)",

  messageBubble: {
    padding: "14px 16px",
    borderRadius: 22,
    wordBreak: "break-word",
  },

  userBubble: {
    background: "linear-gradient(145deg,#2563eb,#4f46e5)",
    color: "#fff",
    borderBottomRightRadius: 6,
    boxShadow: "0 16px 35px rgba(37,99,235,0.22)",
  },

  assistantBubble: {
    background: "#ffffff",
    color: "#0f172a",
    borderBottomLeftRadius: 6,
    border: "1px solid #e5e7eb",
    boxShadow: "0 18px 45px rgba(15,23,42,0.07)",
  },

  messageHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 7,
  },

  messageHeaderRight: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginLeft: "auto",
    flexShrink: 0,
  },

  orderCard: {
    width: "100%",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 16px 40px rgba(15,23,42,0.06)",
  },

  orderCardHeader: {
    padding: "18px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderBottom: "1px solid #eef2f7",
    background:
      "linear-gradient(180deg, rgba(255,255,255,1), rgba(248,250,252,0.88))",
  },

  orderTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: "#111827",
    letterSpacing: "-0.02em",
  },

  orderSubtitle: {
    marginTop: 3,
    fontSize: 12,
    color: "#64748b",
  },

  liveStatus: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: 12,
    color: "#64748b",
    whiteSpace: "nowrap",
  },

  liveDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#10b981",
    boxShadow: "0 0 0 4px rgba(16,185,129,0.12)",
  },

  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    padding: "16px 20px",
  },

  metricCard: {
    minHeight: 76,
    borderRadius: 18,
    border: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px",
    minWidth: 0,
  },

  metricCard_blue: {
    background: "linear-gradient(145deg,#eff6ff,#ffffff)",
    borderColor: "#bfdbfe",
  },

  metricCard_green: {
    background: "linear-gradient(145deg,#ecfdf5,#ffffff)",
    borderColor: "#bbf7d0",
  },

  metricCard_orange: {
    background: "linear-gradient(145deg,#fff7ed,#ffffff)",
    borderColor: "#fed7aa",
  },

  metricCard_purple: {
    background: "linear-gradient(145deg,#f5f3ff,#ffffff)",
    borderColor: "#ddd6fe",
  },

  metricIcon: {
    width: 40,
    height: 40,
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    flexShrink: 0,
  },

  metricIcon_blue: {
    color: "#2563eb",
    background: "#dbeafe",
  },

  metricIcon_green: {
    color: "#16a34a",
    background: "#dcfce7",
  },

  metricIcon_orange: {
    color: "#ea580c",
    background: "#ffedd5",
  },

  metricIcon_purple: {
    color: "#7c3aed",
    background: "#ede9fe",
  },

  metricValue: {
    fontSize: 18,
    lineHeight: "22px",
    fontWeight: 800,
    color: "#111827",
    letterSpacing: "-0.02em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  metricLabel: {
    marginTop: 4,
    fontSize: 12,
    color: "#64748b",
    whiteSpace: "nowrap",
  },

  compactTable: {
    borderTop: "1px solid #eef2f7",
  },

  compactTableHead: {
    display: "grid",
    gridTemplateColumns: "1.2fr 1.4fr 0.8fr 0.9fr",
    gap: 14,
    padding: "13px 20px",
    fontSize: 12,
    color: "#64748b",
    fontWeight: 700,
    background: "#f8fafc",
    borderBottom: "1px solid #eef2f7",
  },

  orderRow: {
    width: "100%",
    border: "none",
    background: "#ffffff",
    display: "grid",
    gridTemplateColumns: "1.2fr 1.4fr 0.8fr 0.9fr",
    gap: 14,
    alignItems: "center",
    padding: "14px 20px",
    textAlign: "left",
    borderBottom: "1px solid #eef2f7",
    cursor: "pointer",
  },

  orderNoBlock: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  orderNo: {
    color: "#2563eb",
    fontWeight: 800,
    fontSize: 13,
  },

  orderCustomer: {
    color: "#94a3b8",
    fontSize: 11,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  productBlock: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  productName: {
    color: "#111827",
    fontWeight: 700,
    fontSize: 13,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  productQty: {
    color: "#94a3b8",
    fontSize: 11,
  },

  amountText: {
    color: "#111827",
    fontWeight: 800,
    fontSize: 13,
    whiteSpace: "nowrap",
  },

  orderFooter: {
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    background: "#ffffff",
  },

  viewAllButton: {
    height: 38,
    borderRadius: 12,
    color: "#4f46e5",
    borderColor: "#c7d2fe",
    background: "#ffffff",
    fontWeight: 700,
  },

  exportButton: {
    height: 38,
    borderRadius: 12,
    color: "#334155",
    borderColor: "#e2e8f0",
    background: "#ffffff",
    fontWeight: 700,
  },

  statusProcessing: {
    margin: 0,
    borderRadius: 999,
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#2563eb",
    fontWeight: 700,
  },

  statusSuccess: {
    margin: 0,
    borderRadius: 999,
    border: "1px solid #bbf7d0",
    background: "#ecfdf5",
    color: "#16a34a",
    fontWeight: 700,
  },

  statusDanger: {
    margin: 0,
    borderRadius: 999,
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#dc2626",
    fontWeight: 700,
  },

  statusWarning: {
    margin: 0,
    borderRadius: 999,
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#ea580c",
    fontWeight: 700,
  },

  statusNeutral: {
    margin: 0,
    borderRadius: 999,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    color: "#475569",
    fontWeight: 700,
  },

  genericList: {
    display: "flex",
    flexDirection: "column",
  },

  genericRow: {
    padding: "14px 20px",
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    borderBottom: "1px solid #eef2f7",
  },

  genericCell: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  genericLabel: {
    fontSize: 11,
    color: "#94a3b8",
    fontWeight: 700,
  },

  genericValue: {
    fontSize: 13,
    color: "#111827",
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  inlineSuggestions: {
    marginTop: 12,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },

  inlineSuggestionButton: {
    borderRadius: 999,
    borderColor: "#dbeafe",
    color: "#2563eb",
    background: "#ffffff",
  },

  quickPromptBar: {
    display: "flex",
    gap: 8,
    padding: "12px 20px 0",
    borderTop: "1px solid #e5e7eb",
    background: "#ffffff",
    overflowX: "auto",
  },

  quickPromptButton: {
    flexShrink: 0,
    borderRadius: 999,
    border: "1px solid #dbeafe",
    background: "#f8fafc",
    color: "#2563eb",
    fontWeight: 700,
  },

  panelFooter: {
    padding: 20,
    paddingTop: 12,
    background: "#ffffff",
  },

  inputWrapper: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
  },

  textArea: {
    flex: 1,
    borderRadius: 24,
    padding: "12px 18px",
    borderColor: "#e2e8f0",
    boxShadow: "none",
  },

  sendButton: {
    width: 46,
    height: 46,
    background: "linear-gradient(145deg,#635bff,#2563eb)",
    border: "none",
    boxShadow: "0 14px 30px rgba(37,99,235,0.28)",
  },

  footerInfo: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 9,
  },

  footerText: {
    fontSize: 11,
    color: "#64748b",
  },

  keyHint: {
    background: "#f8fafc",
    padding: "2px 6px",
    borderRadius: 7,
    border: "1px solid #e2e8f0",
    color: "#64748b",
  },
};